import React, {useEffect, useState} from 'react';
import {Button, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {Outlet, useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import NewTableComponent from '@components/table/NewTable.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {finalize, Observable} from 'rxjs';
import {Add} from '@icons/index';
import {Account, accountsQuery, accountsService, searchAccountEffect} from '@store/settings/account';
import {TaxRule} from '@store/common/organisation.enums';
import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import ConfirmModal from '@components/modal/Confirm.modal';
import dayjs from 'dayjs';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';

const AccountTab = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const searchAccounts = useEffectFn(searchAccountEffect);
  const [{accounts, loading}] = useObservable(accountsQuery.accounts$);
  const [selectedAccounts] = useObservable(accountsQuery.selectedAccounts$);
  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);


  const [filters] = useObservable(accountsQuery.filters$);
  const [sortModel] = useObservable(accountsQuery.sort$);
  const [tablePagination] = useObservable(accountsQuery.pagination$);

  useEffect(() => {
    searchAccounts({filters, tablePagination, sort: sortModel});
  }, [filters, searchAccounts, sortModel, tablePagination]);

  const handleConfirmValidation = (type: OtherActionType, account: Account) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = accountsService.deleteAccountInList(account.id);
        break;

    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.account`), {variant: 'success'});
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <>
      <Stack flex={1} width="100%" spacing={2} p="25px">
        <Stack justifyContent="space-between" direction="row" alignItems="center" spacing={2}>
          <Typography fontSize="30px" fontWeight="700">
            {t('routes.accounting')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add/>}
            onClick={() => navigate('add', {replace: true})}>
            {t('settings.account.addButton')}
          </Button>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <CheckboxComponent label={t('settings.account.table.hideExpired')} value={filters.hideExpired}
                             handleChange={accountsService.setFilters('hideExpired')}/>
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT_BOLD({
              field: 'id',
              label: t('settings.account.table.id'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Account>) => params.row.id,
              width: 100
            }),
            COLUMN_TYPES.TEXT({
              field: 'taxRule',
              label: t('settings.account.table.taxRule'),
              valueGetter: (params: GridValueGetterParams<any, Account>) => params.row.taxRule,
              valueFormatter: (params) => TaxRule.selectItems().find((s) => s.value === params.value)?.label || '',
              minWidth: 120,
            }),
            COLUMN_TYPES.TEXT({
              field: 'serviceKind?.kind',
              label: t('settings.account.table.serviceKind'),
              valueGetter: (params: GridValueGetterParams<any, Account>) => params.row.serviceKind?.kind,
              fullWidth: true,
            }),
            COLUMN_TYPES.TEXT({
              field: 'validFromDate',
              label: t('settings.account.table.validFromDate'),
              valueGetter: (params: GridValueGetterParams<any, Account>) => params.row.validFromDate,
              valueFormatter: (params) => dayjs(params.value).format('DD.MM.YYYY'),
            }),
            COLUMN_TYPES.TEXT({
              field: 'validToDate',
              label: t('settings.account.table.validToDate'),
              valueGetter: (params: GridValueGetterParams<Account, Account>) => params.row.validToDate,
              valueFormatter: (params) => dayjs(params.value).format('DD.MM.YYYY'),
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (account: Account) => navigate(`${account.id}`),
              otherActions: [
                {
                  type: OtherActionType.DUPLICATE,
                  handleClick: (account: Account) => navigate('add', {state: account}),
                },
                {
                  type: OtherActionType.DELETE,
                  handleClick: (account: Account) => setOpenConfirmAction({type: OtherActionType.DELETE, row: account}),
                },

              ]
            }),
          ]}
          handlePageSizeChange={(value) => {
            accountsService.setPagination('limit')(value);
            accountsService.setPagination('page')(1);
          }}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => accountsService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={accountsService.setSort}
          sortModel={[sortModel]}
          handleRowSelected={accountsService.setSelectedAccountIds}
          rowsSelected={selectedAccounts}
          disableRowSelection
          totalRows={accountsQuery.totalRows}
          data={accounts}/>
      </Stack>
      <Outlet/>
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.client`)}
          handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)}/>
      )}
    </>
  );
};

export default AccountTab;
